import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Button,
  IconButton,
  Chip,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import {
  School as SchoolIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminIcon,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  SupervisorAccount as SupervisorIcon,
  Storage as StorageIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import SchoolManagement from "./admin-panel/SchoolManagement";
import UserManagement from "./admin-panel/UserManagement";
import SchoolRequestManagement from "./admin-panel/SchoolRequestManagement";
import { useSchools } from "../../hooks/useSchools";
import { useUsers } from "../../hooks/useUsers";
import { useAuth } from "../../contexts/AuthContext";
import { useBackup } from "../../hooks/useBackup";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";
import EnhancedTable from "../../components/common/EnhancedTable";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();
  // Enable schools query fetch for dashboard statistics
  const { schoolsQuery } = useSchools(false, { enableQuery: true });
  const { usersQuery } = useUsers();
  const {
    initiateBackupMutation,
    downloadBackupMutation,
    deleteBackupMutation,
    backupExecutionLogsQuery,
  } = useBackup();
  const { t } = useTranslation();
  const { showSuccess, showError } = useError();

  const pad2 = (n: number) => String(n).padStart(2, "0");

  const parseBackendDateTime = (
    value: unknown
  ): { y: number; m: number; d: number; hh: number; mm: number; ss: number } | null => {
    if (!value) return null;

    // Handles backend LocalDateTime serialization like: "2026,3,17,11,9,21,371284000"
    const raw = String(value).trim();
    if (raw.includes(",")) {
      const parts = raw.split(",").map((p) => Number(p.trim()));
      if (parts.length >= 6 && parts.slice(0, 6).every((n) => Number.isFinite(n))) {
        const [y, m, d, hh, mm, ss] = parts;
        return { y, m, d, hh, mm, ss };
      }
    }

    // Fallback: ISO / other date strings
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return null;
    return {
      y: dt.getFullYear(),
      m: dt.getMonth() + 1,
      d: dt.getDate(),
      hh: dt.getHours(),
      mm: dt.getMinutes(),
      ss: dt.getSeconds(),
    };
  };

  const formatTimeHms = (value: unknown): string => {
    const p = parseBackendDateTime(value);
    if (!p) return value ? String(value) : "-";
    // As requested: "11:9:21" (no zero padding)
    return `${p.hh}:${p.mm}:${p.ss}`;
  };

  const formatDateTimeDdMmYyyyHms = (value: unknown): string => {
    const p = parseBackendDateTime(value);
    if (!p) return value ? String(value) : "-";
    return `${pad2(p.d)}-${pad2(p.m)}-${p.y} ${p.hh}:${p.mm}:${p.ss}`;
  };

  const toMillis = (value: unknown): number => {
    const p = parseBackendDateTime(value);
    if (!p) return 0;
    return new Date(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss).getTime();
  };

  const sortedBackupLogs = [...(backupExecutionLogsQuery.data ?? [])].sort(
    (a, b) => toMillis(b.requestDate) - toMillis(a.requestDate)
  );

  const totalSchools = schoolsQuery.data?.length || 0;
  const totalUsers = usersQuery.data?.length || 0;
  const activeUsers = usersQuery.data?.filter((u) => u.status)?.length || 0;
  const schoolAdmins =
    usersQuery.data?.filter((u) => u.roles.includes("SCHOOL_ADMIN"))?.length ||
    0;
  const teachers =
    usersQuery.data?.filter((u) => u.roles.includes("TEACHER"))?.length || 0;
  const students =
    usersQuery.data?.filter((u) => u.roles.includes("STUDENT"))?.length || 0;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const statCards = [
    {
      title: t("total_schools"),
      value: totalSchools,
      icon: <SchoolIcon sx={{ fontSize: 40 ,color:"#187bcc"}} />,
      color: "#84bff0",
    },
    {
      title: t("total_users"),
      value: totalUsers,
      icon: <PeopleIcon sx={{ fontSize: 40 ,color:"#187bcc"}} />,
      color: "#84bff0",
    },
    {
      title: t("active_users"),
      value: activeUsers,
      icon: <SecurityIcon sx={{ fontSize: 40 ,color:"#187bcc"}} />,
      color: "#84bff0",
    },
    {
      title: t("school_admins"),
      value: schoolAdmins,
      icon: <SupervisorIcon sx={{ fontSize: 40 ,color:"#187bcc"}} />,
      color: "#84bff0",
    },
    {
      title: t("teachers"),
      value: teachers,
      icon: <PersonIcon sx={{ fontSize: 40 ,color:"#187bcc"}} />,
      color: "#84bff0",
    },
    {
      title: t("students"),
      value: students,
      icon: <GroupIcon sx={{ fontSize: 40 ,color:"#187bcc"}} />,
      color: "#84bff0",
    },
  ];

  if (schoolsQuery.isLoading || usersQuery.isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "#3092e3",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("/schools1.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        position: "relative",
      }}
    >
      <Box sx={{ p: 3, position: "relative", zIndex: 1 }}>
        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <Card
                sx={{
                  height: "100%",
                  background: stat.color,
                  color: "#fff",
                  borderRadius: 4,
                  boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                  },
                }}
              >
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mb: 2 }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography variant="h3" fontWeight="bold" sx={{ mb: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "black",
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  >
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Main Content Tabs */}
        <Paper
          sx={{
            borderRadius: 4,
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "#3092e3",
            boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="admin tabs"
              sx={{
                "& .MuiTab-root": {
                  fontSize: 16,
                  fontWeight: "bold",
                  textTransform: "none",
                  minHeight: 64,
                  padding: "30px",
                  color: "#3092e3",
                },
              }}
            >
              {/* <Tab
                icon={<DashboardIcon />}
                label={t("overview")}
                iconPosition="start"
              /> */}
              <Tab
                icon={<PeopleIcon />}
                label={t("user_management")}
                iconPosition="start"
              />
              <Tab
                icon={<BusinessIcon />}
                label={t("school_management")}
                iconPosition="start"
              />
              <Tab
                icon={<StorageIcon />}
                label={t("backup")}
                iconPosition="start"/>
              <Tab
                icon={<AssignmentIcon />}
                label="School Requests"
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* <TabPanel value={tabValue} index={0}>
            <Slide direction="up" in timeout={1000}>
              <Box>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{ mb: 3, color: "#1976d2" }}
                >
                  {t("system_overview")}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        bgcolor: "rgba(25, 118, 210, 0.05)",
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                        System Health
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2">Database</Typography>
                            <Typography variant="body2" color="success.main">
                              Healthy
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={95}
                            color="success"
                          />
                        </Box>
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2">
                              API Services
                            </Typography>
                            <Typography variant="body2" color="success.main">
                              Online
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={98}
                            color="success"
                          />
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Slide>
          </TabPanel> */}

          <TabPanel value={tabValue} index={0}>
            <Box>
              {/* <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#1976d2' }}>
                User Management
              </Typography> */}
              <UserManagement />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box>
              {/* <Typography variant="h4" fontWeight="bold" sx={{ mb: 3, color: '#1976d2' }}>
                School Management
              </Typography> */}
              <SchoolManagement />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box>
              <Box sx={{ mt: 3 }}>
                {backupExecutionLogsQuery.isLoading && <LinearProgress sx={{ mb: 1 }} />}

                {backupExecutionLogsQuery.isError && (
                  <Box sx={{ mb: 1 }}>
                    <Typography color="error" fontWeight="bold">
                      Failed to load backup logs.
                    </Typography>
                  </Box>
                )}

                <EnhancedTable
                  title="Backup Execution Logs"
                  columns={[
                    { id: "triggerType", label: "Trigger Type", minWidth: 140 },
                    {
                      id: "backupStartTime",
                      label: "Backup Start Time",
                      minWidth: 190,
                      format: (value) => formatTimeHms(value),
                    },
                    {
                      id: "status",
                      label: "Status",
                      minWidth: 120,
                      format: (value) => (
                        <Chip
                          size="small"
                          label={String(value)}
                          color={
                            value === "SUCCESS"
                              ? "success"
                              : value === "ERROR"
                              ? "error"
                              : value === "IN_PROGRESS"
                              ? "warning"
                              : "default"
                          }
                          variant="outlined"
                        />
                      ),
                    },
                    { id: "initiatedBy", label: "Initiated By", minWidth: 160, format: (v) => (v ? String(v) : "-") },
                    {
                      id: "requestDate",
                      label: "Request Date",
                      minWidth: 170,
                      format: (value) => formatDateTimeDdMmYyyyHms(value),
                    },
                  ]}
                  data={sortedBackupLogs}
                  searchFields={["triggerType", "status", "initiatedBy", "requestDate"]}
                  actions={(row: any) => (
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                      {row.status === "SUCCESS" && (
                        <IconButton
                          aria-label="Download backup"
                          size="small"
                          sx={{ color: "success.main" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadBackupMutation.mutate(row.requestUuid, {
                              onError: () => {
                                showError("Failed to download backup.");
                              },
                            });
                          }}
                          disabled={downloadBackupMutation.isLoading}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      )}

                      {row.status === "SUCCESS" && (
                        <IconButton
                          aria-label="Delete backup"
                          size="small"
                          sx={{ color: "error.main" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteBackupMutation.mutate(row.requestUuid, {
                              onSuccess: () => {
                                showSuccess("Backup deleted successfully.");
                              },
                              onError: () => {
                                showError("Failed to delete backup.");
                              },
                            });
                          }}
                          disabled={deleteBackupMutation.isLoading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  )}
                  rightActions={
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#3092e3",
                        "&:hover": { backgroundColor: "primary.main" },
                      }}
                      startIcon={
                        initiateBackupMutation.isLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <StorageIcon />
                        )
                      }
                      onClick={() => {
                        initiateBackupMutation.mutate(undefined, {
                          onSuccess: () => {
                            showSuccess("Backup process initiated successfully!");
                          },
                          onError: () => {
                            showError(
                              "Failed to start backup process. Please try again or check logs."
                            );
                          },
                        });
                      }}
                      disabled={initiateBackupMutation.isLoading}
                    >
                      {initiateBackupMutation.isLoading
                        ? "Initiating..."
                        : "Trigger Manual Backup"}
                    </Button>
                  }
                  cardProps={{
                    elevation: 0,
                    sx: {
                      border: "1px solid #e0e0e0",
                      "& .MuiCardContent-root": { p: 2 },
                    },
                  }}
                />
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box>
              <SchoolRequestManagement />
            </Box>
          </TabPanel>
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
